import axiosInstance from './apiinterseptor';

export interface ActiveUser {
  userId: string;
  username: string;
  email: string;
  role: string;
  lastActivity: string;
  currentPage: string;
  ipAddress: string;
  userAgent: string;
  sessionId: string;
  isOnline: boolean;
  loginTime: string;
}

export interface ActiveUsersStats {
  totalActive: number;
  byRole: { [key: string]: number };
  byPage: { [key: string]: number };
  timestamp: string;
}

export interface DailyActivity {
  userId: string;
  username: string;
  email: string;
  role: string;
  activeSeconds: number;
  firstSeenAt: string;
  lastSeenAt: string;
  hitCount: number;
  routes?: {
    path: string;
    timestamp: string;
  }[];
  devices?: ActivityDevice[];
}

export interface DailyActivityHistoryEntry {
  date: string;
  activeSeconds: number;
  firstSeenAt: string;
  lastSeenAt: string;
  hitCount: number;
  routes?: {
    path: string;
    timestamp: string;
  }[];
  devices?: ActivityDevice[];
}

export interface DailyActivityHistoryResponse {
  records: DailyActivityHistoryEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  timestamp: string;
}

export interface ActivityDevice {
  userAgent: string;
  deviceType: 'mobile' | 'tablet' | 'desktop' | 'other';
  timestamp: string;
}

export interface ActiveUsersResponse {
  activeUsers: ActiveUser[];
  count: number;
  total: number;
  page: number;
  totalPages: number;
  limit: number;
  timestamp: string;
}

export interface DailyActivityResponse {
  activities: DailyActivity[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
  timestamp: string;
}

class ActiveUserService {
  private updateInterval: NodeJS.Timeout | null = null;
  private isTracking = false;

  /**
   * Start tracking user activity
   */
  startTracking(): void {
    if (this.isTracking) return;

    this.isTracking = true;
    
    // Update activity immediately
    this.updateUserActivity();
    
    // Update activity every 30 seconds
    this.updateInterval = setInterval(() => {
      this.updateUserActivity();
    }, 30000);

    // Update activity on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateUserActivity();
      }
    });

    // Update activity before page unload
    window.addEventListener('beforeunload', () => {
      this.markUserOffline();
    });
  }

  /**
   * Stop tracking user activity
   */
  stopTracking(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isTracking = false;
    this.markUserOffline();
  }

  /**
   * Update user activity
   */
  async updateUserActivity(currentPage?: string): Promise<void> {
    try {
      const page = currentPage || window.location.pathname;
      
      await axiosInstance.post('/active-users/update', {
        currentPage: page
      });
    } catch (error) {
      console.error('Failed to update user activity:', error);
    }
  }

  /**
   * Mark user as offline
   */
  async markUserOffline(): Promise<void> {
    try {
      await axiosInstance.post('/active-users/offline');
    } catch (error) {
      console.error('Failed to mark user offline:', error);
    }
  }

  /**
   * Get all active users (Admin only)
   */
  async getActiveUsers(params: { page?: number; limit?: number } = {}): Promise<ActiveUsersResponse> {
    try {
      const response = await axiosInstance.get('/active-users', {
        params
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to get active users:', error);
      throw error;
    }
  }

  /**
   * Get active users by role (Admin only)
   */
  async getActiveUsersByRole(
    role: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<ActiveUsersResponse> {
    try {
      const response = await axiosInstance.get(`/active-users/role/${role}`, {
        params
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to get active users by role:', error);
      throw error;
    }
  }

  /**
   * Get active user count
   */
  async getActiveUserCount(): Promise<number> {
    try {
      const response = await axiosInstance.get('/active-users/count');
      return response.data.data.activeUserCount;
    } catch (error) {
      console.error('Failed to get active user count:', error);
      throw error;
    }
  }

  /**
   * Get active users statistics (Admin only)
   */
  async getActiveUsersStats(): Promise<ActiveUsersStats> {
    try {
      const response = await axiosInstance.get('/active-users/stats');
      return response.data.data;
    } catch (error) {
      console.error('Failed to get active users stats:', error);
      throw error;
    }
  }

  /**
   * Get current user's activity status
   */
  async getUserActivityStatus(): Promise<{ isActive: boolean; userActivity: ActiveUser | null }> {
    try {
      const response = await axiosInstance.get('/active-users/status');
      return response.data.data;
    } catch (error) {
      console.error('Failed to get user activity status:', error);
      throw error;
    }
  }

  /**
   * Check if tracking is active
   */
  isTrackingActive(): boolean {
    return this.isTracking;
  }

  async getDailyActivity(params: {
    date?: string;
    role?: string;
    page?: number;
    limit?: number;
  }): Promise<DailyActivityResponse> {
    try {
      const response = await axiosInstance.get('/active-users/daily-activity', {
        params
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to get daily activity:', error);
      throw error;
    }
  }

  async getDailyActivityHistory(
    userId: string,
    params: { page?: number; limit?: number }
  ): Promise<DailyActivityHistoryResponse> {
    try {
      const response = await axiosInstance.get('/active-users/daily-activity/history', {
        params: {
          userId,
          ...params
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to get daily activity history:', error);
      throw error;
    }
  }
}

// Create singleton instance
const activeUserService = new ActiveUserService();

export default activeUserService;
